<template>
  <div v-if="!loading">
    <form>
      <!-- Header with Info -->
      <v-card
        class="mb-4"
        outlined
      >
        <v-card-title class="primary white--text">
          <v-icon
            left
            dark
          >
            mdi-target
          </v-icon>
          Plán resocializačnej pomoci
        </v-card-title>
        <v-alert
          dense
          text
          color="info"
          icon="mdi-information-outline"
          class="ma-4"
        >
          <div class="text-caption">
            <strong>Plánovanie:</strong> Pri plánovaní je možné prostredníctvom +pridať viac cieľov (cieľom môže byť zabezpečiť bývanie, obnoviť kontakty s rodinou, riešenie závislosti, ...)<br>
            <strong>Úlohy:</strong> Ide o čiastklové ciele ktoré zabezpečia dosiahnutie hlavného cieľa<br>
            <strong>Evaluácia:</strong> <em>Splnená</em> - úloha splnená | <em>Čiastočne splnená</em> - sú podniknuté kroky k splneniu úlohy avšak nie je úplne splnená | <em>Nezrealizovaná</em> - úlohu nezačal ešte nikto riešiť
          </div>
        </v-alert>
        <v-card-text
          v-if="form.ciele.length > 0"
          class="pa-4"
        >
          <v-chip-group>
            <v-chip
              small
              outlined
            >
              <v-icon
                left
                small
              >
                mdi-target
              </v-icon>
              {{ goalCountText }}
            </v-chip>
            <v-chip
              small
              outlined
              color="success"
            >
              <v-icon
                left
                small
              >
                mdi-check-circle
              </v-icon>
              {{ completedTasksCount }} splnených
            </v-chip>
            <v-chip
              small
              outlined
              color="warning"
            >
              <v-icon
                left
                small
              >
                mdi-progress-clock
              </v-icon>
              {{ partialTasksCount }} čiastočne
            </v-chip>
            <v-chip
              small
              outlined
              color="grey"
            >
              <v-icon
                left
                small
              >
                mdi-circle-outline
              </v-icon>
              {{ unrealizedTasksCount }} nezrealizovaných
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>

      <!-- Goals List -->
      <v-card
        v-for="(item, index1) in form.ciele"
        :key="index1"
        class="mb-4"
        elevation="2"
      >
        <!-- Goal Header -->
        <v-card-title class="indigo lighten-5">
          <v-icon
            left
            color="indigo"
          >
            mdi-flag-variant
          </v-icon>
          <span class="text-h6">Cieľ {{ index1 + 1 }}</span>
          <v-spacer />
          <v-chip
            v-if="item.ulohy && item.ulohy.length > 0"
            small
            :color="getGoalProgressColor(item)"
            text-color="white"
          >
            {{ getGoalProgress(item) }}% splnené
          </v-chip>
          <v-btn
            v-if="!form.closed"
            icon
            color="error"
            @click="confirmDeleteGoal(index1)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Goal Input -->
        <v-card-text class="pb-0">
          <v-textarea
            v-model="item.ciel"
            :disabled="form.closed"
            label="Popis cieľa"
            placeholder="Napr.: Zabezpečiť bývanie, Obnoviť kontakty s rodinou, Riešenie závislosti..."
            outlined
            auto-grow
            rows="2"
            hide-details
          >
            <template v-slot:prepend>
              <v-icon color="indigo">
                mdi-text
              </v-icon>
            </template>
          </v-textarea>
        </v-card-text>

        <!-- Tasks List -->
        <v-card-text v-if="item.ulohy && item.ulohy.length > 0">
          <v-divider class="my-4" />
          <div class="text-subtitle-1 font-weight-medium mb-3">
            <v-icon
              left
              small
            >
              mdi-format-list-checks
            </v-icon>
            Úlohy ({{ item.ulohy.length }})
          </div>

          <v-timeline
            dense
            align-top
          >
            <v-timeline-item
              v-for="(input, index2) in item.ulohy"
              :key="index2"
              :color="getTaskColor(input.evaluacia)"
              :icon="getTaskIcon(input.evaluacia)"
              fill-dot
              small
            >
              <v-card outlined>
                <v-card-title class="py-2 subtitle-2">
                  <v-icon
                    left
                    small
                    :color="getTaskColor(input.evaluacia)"
                  >
                    {{ getTaskIcon(input.evaluacia) }}
                  </v-icon>
                  Úloha {{ index2 + 1 }}
                  <v-spacer />
                  <v-chip
                    v-if="input.evaluacia"
                    x-small
                    :color="getTaskColor(input.evaluacia)"
                    text-color="white"
                  >
                    {{ input.evaluacia }}
                  </v-chip>
                  <v-btn
                    v-if="!form.closed"
                    icon
                    x-small
                    color="error"
                    class="ml-2"
                    @click="confirmDeleteTask(index1, index2)"
                  >
                    <v-icon small>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </v-card-title>

                <v-card-text>
                  <!-- Task Description -->
                  <v-textarea
                    v-model="input.navrh_uloh"
                    :disabled="form.closed"
                    label="Návrh úlohy"
                    placeholder="Detailný popis úlohy potrebnej na splnenie cieľa"
                    outlined
                    auto-grow
                    rows="2"
                    dense
                    hide-details
                    class="mb-3"
                  />

                  <!-- Participants Row -->
                  <v-row dense>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="input.participuje"
                        :disabled="form.closed"
                        label="Participuje (osoba)"
                        outlined
                        dense
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small>
                            mdi-account
                          </v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="input.participujuca_organizacia"
                        :disabled="form.closed"
                        label="Participujúca organizácia"
                        outlined
                        dense
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small>
                            mdi-domain
                          </v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Dates and Evaluation Row -->
                  <v-row
                    dense
                    class="mt-3"
                  >
                    <v-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-menu
                        v-model="input.termin_stavovenia_date_sync"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="input.termin_stavovenia"
                            label="Termín stanovenia"
                            outlined
                            dense
                            hide-details
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon small>
                                mdi-calendar-start
                              </v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="input.termin_stavovenia"
                          first-day-of-week="1"
                          @input="input.termin_stavovenia_date_sync = false"
                        />
                      </v-menu>
                    </v-col>

                    <v-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-menu
                        v-model="input.termin_splnenia_date_sync"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="input.termin_splnenia"
                            label="Termín splnenia"
                            outlined
                            dense
                            hide-details
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon small>
                                mdi-calendar-check
                              </v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="input.termin_splnenia"
                          first-day-of-week="1"
                          @input="input.termin_splnenia_date_sync = false"
                        />
                      </v-menu>
                    </v-col>

                    <v-col
                      cols="12"
                      md="4"
                    >
                      <v-select
                        v-model="input.evaluacia"
                        :disabled="form.closed"
                        label="Evaluácia"
                        :items="selects.evaluacia"
                        outlined
                        dense
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small>
                            mdi-clipboard-check
                          </v-icon>
                        </template>
                        <template v-slot:item="{ item: evaluaciaItem }">
                          <v-icon
                            left
                            small
                            :color="getTaskColor(evaluaciaItem)"
                          >
                            {{ getTaskIcon(evaluaciaItem) }}
                          </v-icon>
                          {{ evaluaciaItem }}
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>

        <!-- Add Task Button -->
        <v-card-actions v-if="!form.closed">
          <v-btn
            text
            color="primary"
            @click="addRowUloha(index1)"
          >
            <v-icon left>
              mdi-plus-circle
            </v-icon>
            Pridať úlohu
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Add Goal Button -->
      <v-btn
        v-if="!form.closed"
        block
        outlined
        large
        color="primary"
        class="mb-4"
        @click="addRowCiel()"
      >
        <v-icon left>
          mdi-plus-circle
        </v-icon>
        Pridať nový cieľ
      </v-btn>

      <v-card class="my-5">
        <v-card-title>
          <v-icon left color="primary">mdi-chart-line</v-icon>
          Vyhodnotenie plánu resocializačnej pomoci
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-text-field
                v-model="form.datum_vyhodnotenia"
                disabled
                label="Dátum vyhodnotenia"
                name="Dátum vyhodnotenia"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="form.vyhodnotenie_popis"
                :disabled="form.closed"
                label="Popis"
                name="Popis"
                auto-grow
                rows="1"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Form Actions with PDF export and unsaved changes tracking -->
      <div
        v-if="!form.closed"
        class="d-flex align-center pt-5"
      >
        <v-btn
          color="secondary"
          @click="downloadItem"
        >
          <v-icon left>
            mdi-file-pdf-box
          </v-icon>
          PDF export
        </v-btn>
        <v-spacer />
        <FormActions
          :is-dirty="isDirty"
          :loading="loading"
          :fixed="false"
          save-text="Uložiť"
          @save="updateItem"
        />
      </div>
    </form>

    <!-- Info Dialog -->
    </div>

  <div v-else>
    <form-skeleton-component skeleton_type="PotrebyPlan" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api';
import { useRoute } from '@/composables/useRouter.js';
import { APIService } from "@/http/APIService.js";
import FormSkeleton from "../components/FormSkeleton.vue";
import FormActions from "@/components/FormActions.vue";
import { useIapszForm } from "@/composables/useIapszForm.js";
import { useFormDirty } from "@/composables/useFormDirty.js";

const apiService = new APIService();

export default {
  components: {
    "form-skeleton-component": FormSkeleton,
    FormActions,
  },

  setup() {
    const route = useRoute();
    
    // Use composables
    const { loading, setLoading, handleSaveSuccess, handleSaveError } = useIapszForm();

    // Form actions ref
    const formActions = ref(null);

    // Form state
    const form = ref({
      id: null,
      datum_vyhodnotenia: "",
      vyhodnotenie_popis: "",
      ciele: [],
      closed: false,
    });
    
    // Track dirty state
    const initialForm = ref({});
    const { isDirty, markAsPristine, enableBrowserWarning } = useFormDirty(initialForm, form, { deep: true });
    enableBrowserWarning();

    const selects = reactive({
      evaluacia: ["Splnená", "Čiastočne splnená", "Nezrealizovaná"],
    });

    // Manual/help text - configured for useInfoDialog
    const manual = reactive({
      plan: {
        nazov: "Plán",
        charakteristika: [
          "Pri plánovaní je možné prostredníctvom +pridať viac cieľov. Cieľom môže byť zabezpečiť bývanie, Obnoviť kontakty s rodinou, Riešenie závislosti, ...)",
          "Návrh úloh pre splnenie cieľa - Ide o čiastkové ciele ktoré zabezpečia dosiahnutie hlavného cieľa (Pri zabezpečení bývania to môže byť: vybavenie OP, požiadať obec o jednorazovú dávku pomoci v HN a pod.)",
          "Participuje - Uvádza sa osoba, ktorá pomáha v pri riešení stanovenej úlohy (v prípade že osoba pomáha v naplnení úlohy)",
          "Participujúca organizácia - Uvádza sa organizácia, ktorá pomáha v pri riešení stanovenej úlohy (v prípade že organizácia pomáha pri naplnení úlohy)",
          "Termín stanovenia úlohy - Uvádza sa termín, do kedy je potrebné úlohu splniť.",
          "Termín splnenia úlohy - Uvádza sa reálny dátum kedy bola úloha splnená.",
          "Evaluácia: Splnená – keď je úloha splnená označí sa ako Splnená. Čiastočne splnená – označí sa v prípade ak sú podniknuté kroky k splneniu úlohy avšak úloha ešte nebola úplne splnená. (napr. Podaná žiadosť o finančný príspevok, poslané doklady, chýba však napr. motivačný list pre nakladanie s finančným príspevkom). Nezrealizovaná – označí sa ak úlohu nezačal  ešte nikto riešiť."
        ],
      },

      vyhodnotenie_planu: {
        nazov: "Vyhodnotenie plánu resocializačnej pomoci",
        karakteristika: ["Vyhodnotenie je dôležité spracovať do termínu prepustenia nakoľko je vyhodnotenie súčasťou vyhodnotenia programu zaobchádzania."]
      }
    });
    
    // Initialize info dialog with manual data
    // Methods
    const downloadItem = () => {
      apiService.getIapszPotrebyPlanExport(route.params.id, form.value.id).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "iapsz_" + route.params.id + "_export_plan" + ".pdf"
        );
        document.body.appendChild(link);
        link.click();
      }).catch(
        (error) => {
          handleSaveError();
        });
    };

    const getItem = () => {
      setLoading(true);
      apiService.getIapszPotrebyPlan(route.params.id).then((response) => {
        form.value = response;
        initialForm.value = JSON.parse(JSON.stringify(response));
        markAsPristine();
        setLoading(false);
      });
    };

    const updateItem = () => {
      apiService.updateIapszPotrebyPlan(form.value, route.params.id).then(
        (response) => {
          handleSaveSuccess();
          form.value = response;
          markAsPristine();
          if (formActions.value) {
            formActions.value.showSuccess();
          }
        },
        () => {
          handleSaveError();
          if (formActions.value) {
            formActions.value.showError();
          }
        }
      );
    };

    const addRowCiel = () => {
      form.value.ciele.push({
        potreby_plan: form.value.id,
        ciel: null,
        splneny: null,
        vyznamna_zmena: null,
        ulohy: [],
      });
      updateItem();
    };
    
    const deleteRowCiel = (index) => {
      form.value.ciele.splice(index, 1);
    };

    const addRowUloha = (index) => {
      form.value.ciele[index].ulohy.push({
        ciel: form.value.ciele[index].id,
        poradie: null,
        navrh_uloh: null,
        participuje: null,
        participujuca_organizacia: null,
        termin_stavovenia: null,
        termin_splnenia: null,
        evaluacia: null,
      });
      updateItem();
    };
    
    const deleteRowUloha = (index1, index2) => {
      form.value.ciele[index1].ulohy.splice(index2, 1);
    };

    // UI Helper Methods
    const getTaskColor = (evaluacia) => {
      if (!evaluacia) return 'grey';
      if (evaluacia === 'Splnená') return 'success';
      if (evaluacia === 'Čiastočne splnená') return 'warning';
      return 'grey';
    };

    const getTaskIcon = (evaluacia) => {
      if (!evaluacia) return 'mdi-circle-outline';
      if (evaluacia === 'Splnená') return 'mdi-check-circle';
      if (evaluacia === 'Čiastočne splnená') return 'mdi-progress-clock';
      return 'mdi-circle-outline';
    };

    const getGoalProgress = (goal) => {
      if (!goal.ulohy || goal.ulohy.length === 0) return 0;
      const completed = goal.ulohy.filter(u => u.evaluacia === 'Splnená').length;
      const partial = goal.ulohy.filter(u => u.evaluacia === 'Čiastočne splnená').length;
      return Math.round(((completed + partial * 0.5) / goal.ulohy.length) * 100);
    };

    const getGoalProgressColor = (goal) => {
      const progress = getGoalProgress(goal);
      if (progress >= 75) return 'success';
      if (progress >= 40) return 'warning';
      return 'grey';
    };

    const completedTasksCount = computed(() => {
      return form.value.ciele.reduce((sum, goal) => {
        return sum + (goal.ulohy?.filter(u => u.evaluacia === 'Splnená').length || 0);
      }, 0);
    });

    const partialTasksCount = computed(() => {
      return form.value.ciele.reduce((sum, goal) => {
        return sum + (goal.ulohy?.filter(u => u.evaluacia === 'Čiastočne splnená').length || 0);
      }, 0);
    });

    const unrealizedTasksCount = computed(() => {
      return form.value.ciele.reduce((sum, goal) => {
        return sum + (goal.ulohy?.filter(u => u.evaluacia === 'Nezrealizovaná' || !u.evaluacia).length || 0);
      }, 0);
    });

    const goalCountText = computed(() => {
      const count = form.value.ciele.length;
      if (count === 1) return `${count} cieľ`;
      if (count >= 2 && count <= 4) return `${count} ciele`;
      return `${count} cieľov`;
    });

    const confirmDeleteGoal = (index) => {
      if (confirm(`Naozaj chcete odstrániť cieľ ${index + 1}? Všetky úlohy budú taktiež odstránené.`)) {
        deleteRowCiel(index);
      }
    };

    const confirmDeleteTask = (goalIndex, taskIndex) => {
      if (confirm(`Naozaj chcete odstrániť úlohu ${taskIndex + 1}?`)) {
        deleteRowUloha(goalIndex, taskIndex);
      }
    };

    // Lifecycle
    onMounted(() => {
      getItem();
    });

    return {
      // State
      loading,
      form,
      selects,
      isDirty,
      formActions,
      
      // Computed
      completedTasksCount,
      partialTasksCount,
      unrealizedTasksCount,
      goalCountText,
      
      // Methods
      updateItem,
      downloadItem,
      addRowCiel,
      deleteRowCiel,
      addRowUloha,
      deleteRowUloha,
      confirmDeleteGoal,
      confirmDeleteTask,
      getTaskColor,
      getTaskIcon,
      getGoalProgress,
      getGoalProgressColor,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      if (window.confirm('Máte neuložené zmeny. Naozaj chcete odísť?')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>